/**
 * postMessage contract for the Design Sandbox live preview.
 *
 * The sandbox admin page (host) embeds the tenant's real storefront in a
 * same-origin iframe loaded with `?design_preview=1&marketing=1` and pushes a
 * candidate theme_config into it. The storefront mounts <DesignPreviewBridge>,
 * which applies the candidate to that frame's session only (never the DB, never
 * other visitors). Mirrors the existing host→app `rumio:design` channel
 * (web/src/theme/extract-tokens.ts) but in the inverse direction.
 */

import type { DesignThemeConfig } from '@/stores/design-preview-store';

/** Host → iframe: apply this candidate theme_config to the preview frame. */
export const DESIGN_PREVIEW_APPLY = 'rumio:design-preview';
/** iframe → host: the bridge mounted and is ready to receive pushes. */
export const DESIGN_PREVIEW_READY = 'rumio:design-preview-ready';

export interface DesignPreviewApplyMessage {
  type: typeof DESIGN_PREVIEW_APPLY;
  themeConfig: DesignThemeConfig | null;
  /** Optional: force light/dark mode in the preview frame. */
  dark?: boolean;
}

export interface DesignPreviewReadyMessage {
  type: typeof DESIGN_PREVIEW_READY;
}

/** Query flag the host appends to the iframe src to activate preview mode. */
export const DESIGN_PREVIEW_QUERY_FLAG = 'design_preview';

/**
 * Append the preview flags to any storefront path so it loads inside the sandbox
 * preview frame. `design_preview=1` activates <DesignPreviewBridge>; `marketing=1`
 * defeats AuthorizedHomeRedirect (which would otherwise bounce an authed admin
 * from `/` into the tenant app on `redirect_authed_to_app` sites — see
 * AuthorizedHomeRedirect.tsx). Preserves any query the caller's path already
 * carries and is idempotent if the flags are already present.
 */
export function buildPreviewUrl(path: string): string {
  const [pathname, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  params.set(DESIGN_PREVIEW_QUERY_FLAG, '1');
  params.set('marketing', '1');
  return `${pathname || '/'}?${params.toString()}`;
}

/**
 * The default iframe src for the live storefront preview (the homepage). Other
 * pages are reached by feeding their path through buildPreviewUrl — see the
 * per-page preset selector in DesignSandbox.
 */
export const DESIGN_PREVIEW_IFRAME_SRC = buildPreviewUrl('/');
